/**
 * Property Tax Rates (Grundsteuer-Hebesätze) for German States
 * 
 * These rates are used to calculate property tax assessments.
 * The values represent average/typical assessment rates per state.
 * 
 * Data structure is designed to be easily migrated to a database table.
 * 
 * Table schema for future database:
 * CREATE TABLE property_tax_rates (
 *   id INT PRIMARY KEY AUTO_INCREMENT,
 *   state_name VARCHAR(50) UNIQUE NOT NULL,
 *   state_code VARCHAR(5) UNIQUE NOT NULL,
 *   tax_rate DECIMAL(5,2) NOT NULL,
 *   description VARCHAR(255),
 *   last_updated DATE,
 *   created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
 * );
 */

export interface PropertyTaxRate {
  stateName: string;
  stateCode: string;
  taxRate: number;
  description?: string;
}

export const PROPERTY_TAX_RATES: PropertyTaxRate[] = [
  {
    stateName: 'Baden-Württemberg',
    stateCode: 'BW',
    taxRate: 3.6,
    description: 'Average property tax rate in Baden-Württemberg'
  },
  {
    stateName: 'Bayern',
    stateCode: 'BY',
    taxRate: 3.1,
    description: 'Average property tax rate in Bavaria'
  },
  {
    stateName: 'Berlin',
    stateCode: 'BE',
    taxRate: 6.0,
    description: 'Average property tax rate in Berlin'
  },
  {
    stateName: 'Brandenburg',
    stateCode: 'BB',
    taxRate: 3.5,
    description: 'Average property tax rate in Brandenburg'
  },
  {
    stateName: 'Bremen',
    stateCode: 'HB',
    taxRate: 3.0,
    description: 'Average property tax rate in Bremen'
  },
  {
    stateName: 'Hamburg',
    stateCode: 'HH',
    taxRate: 4.0,
    description: 'Average property tax rate in Hamburg'
  },
  {
    stateName: 'Hessen',
    stateCode: 'HE',
    taxRate: 4.0,
    description: 'Average property tax rate in Hesse'
  },
  {
    stateName: 'Mecklenburg-Vorpommern',
    stateCode: 'MV',
    taxRate: 3.5,
    description: 'Average property tax rate in Mecklenburg-Vorpommern'
  },
  {
    stateName: 'Niedersachsen',
    stateCode: 'NI',
    taxRate: 3.2,
    description: 'Average property tax rate in Lower Saxony'
  },
  {
    stateName: 'Nordrhein-Westfalen',
    stateCode: 'NW',
    taxRate: 4.0,
    description: 'Average property tax rate in North Rhine-Westphalia'
  },
  {
    stateName: 'Rheinland-Pfalz',
    stateCode: 'RP',
    taxRate: 3.5,
    description: 'Average property tax rate in Rhineland-Palatinate'
  },
  {
    stateName: 'Saarland',
    stateCode: 'SL',
    taxRate: 3.5,
    description: 'Average property tax rate in Saarland'
  },
  {
    stateName: 'Sachsen',
    stateCode: 'SN',
    taxRate: 3.5,
    description: 'Average property tax rate in Saxony'
  },
  {
    stateName: 'Sachsen-Anhalt',
    stateCode: 'ST',
    taxRate: 3.5,
    description: 'Average property tax rate in Saxony-Anhalt'
  },
  {
    stateName: 'Schleswig-Holstein',
    stateCode: 'SH',
    taxRate: 3.5,
    description: 'Average property tax rate in Schleswig-Holstein'
  },
  {
    stateName: 'Thüringen',
    stateCode: 'TH',
    taxRate: 3.5,
    description: 'Average property tax rate in Thuringia'
  }
];

/**
 * Helper function to get tax rate by state name
 * Can be easily replaced with a database query later
 */
export function getPropertyTaxRateByState(stateName: string): number | null {
  const rate = PROPERTY_TAX_RATES.find(
    r => r.stateName.toLowerCase() === stateName.toLowerCase()
  );
  return rate?.taxRate ?? null;
}

/**
 * Helper function to get all state names for dropdown
 */
export function getStateNames(): string[] {
  return PROPERTY_TAX_RATES.map(r => r.stateName).sort();
}

/**
 * Helper function to get state code by name
 */
export function getStateCode(stateName: string): string | null {
  const state = PROPERTY_TAX_RATES.find(
    r => r.stateName.toLowerCase() === stateName.toLowerCase()
  );
  return state?.stateCode ?? null;
}
